import { getRuntimeConfig, getStoredAuthPhone, getStoredAuthToken, hasRuntimeClubId, logMissingClubId } from '@/widgets/booking/config/runtime';
import { http } from './http';
function isSuccessStatus(status) {
    return status >= 200 && status < 300;
}
function logFallback(method, error) {
    console.warn(`[booking-widget] ${method} failed, using fallback`, error);
}
function normalizeApiError(error, fallbackMessage) {
    if (Array.isArray(error)) {
        return error.filter(Boolean).join(', ') || fallbackMessage;
    }
    if (typeof error === 'string' && error.trim().length > 0) {
        return error;
    }
    return fallbackMessage;
}
function getSelectedSubdivisionId(filters = {}) {
    return filters.directionIds?.[0];
}
function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function getBookingWindowEndDate(countWeek) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (countWeek > 0) {
        const endOfWeek = new Date(today);
        const day = endOfWeek.getDay();
        const daysUntilSunday = day === 0 ? 0 : 7 - day;
        endOfWeek.setDate(endOfWeek.getDate() + daysUntilSunday + (countWeek - 1) * 7);
        return formatDate(endOfWeek);
    }
    return `${today.getFullYear()}-12-31`;
}
function getClubId(filters = {}) {
    return filters.clubId ?? getRuntimeConfig().clubId;
}
function ensureClubId(filters = {}) {
    const clubId = getClubId(filters);
    if (!Number.isFinite(clubId) || clubId <= 0) {
        logMissingClubId();
        return null;
    }
    return clubId;
}
function buildGuestClub() {
    const { clubId, club } = getRuntimeConfig();
    if (!clubId) {
        return null;
    }
    return {
        id: clubId,
        name: club.toUpperCase(),
        address: '',
        schedule: '',
        logo: '',
        currency: 'RUB'
    };
}
function buildClubSchedule(payload) {
    const from = payload.club_time_from?.trim();
    const to = payload.club_time_to?.trim();
    if (!from || !to) {
        return '';
    }
    return `${from} - ${to}`;
}
function normalizeClubPhotos(payload) {
    if (!Array.isArray(payload.photo)) {
        return [];
    }
    return payload.photo
        .map((item) => item?.value)
        .filter(Boolean);
}
function normalizeClubDescription(payload) {
    if (typeof payload.description === 'string') {
        return payload.description.trim();
    }
    if (!Array.isArray(payload.description)) {
        return '';
    }
    return payload.description
        .map((item) => {
        if (typeof item === 'string') {
            return item.trim();
        }
        if (item && typeof item.value === 'string') {
            return item.value.trim();
        }
        return '';
    })
        .filter(Boolean)
        .join('\n');
}
function normalizeClubSocials(payload) {
    if (!Array.isArray(payload.social)) {
        return [];
    }
    return payload.social
        .map((item) => ({
        id: item?.id || 0,
        type: typeof item?.description === 'string' ? item.description.trim().toLowerCase() : '',
        url: typeof item?.value === 'string' ? item.value.trim() : ''
    }))
        .filter((item) => item.type && item.url);
}
function mapClub(payload) {
    const photos = normalizeClubPhotos(payload);
    return {
        id: payload.id,
        name: payload.name,
        address: payload.address || '',
        schedule: buildClubSchedule(payload),
        workDays: payload.workDays || '',
        logo: photos[0] || '',
        currency: payload.currency || 'RUB',
        phone: payload.phone || '',
        site: payload.site || '',
        description: normalizeClubDescription(payload),
        photos,
        socials: normalizeClubSocials(payload)
    };
}
function buildGuestUser() {
    const { userId } = getRuntimeConfig();
    const authPhone = getStoredAuthPhone();
    if (authPhone) {
        return {
            id: userId || 0,
            name: authPhone,
            avatar: '',
            contacts: [],
            depositFull: null
        };
    }
    if (!userId) {
        return null;
    }
    return {
        id: userId,
        name: 'Гость',
        avatar: '',
        contacts: [],
        depositFull: null
    };
}
function buildUserName(payload) {
    const fullName = [payload.name, payload.surname]
        .filter((part) => !!part && !!part.trim())
        .join(' ')
        .trim();
    if (fullName) {
        return fullName;
    }
    if (payload.name?.trim()) {
        return payload.name.trim();
    }
    if (payload.nickname?.trim()) {
        return payload.nickname.trim();
    }
    if (payload.phone?.trim()) {
        return payload.phone.trim();
    }
    return 'Пользователь';
}
function normalizeUserAvatar(avatarRaw) {
    if (typeof avatarRaw !== 'string') {
        return '';
    }

    const avatar = avatarRaw.trim();
    if (!avatar) {
        return '';
    }

    if (avatar.startsWith('/')) {
        return avatar.length > 1 ? avatar : '';
    }

    try {
        const parsed = new URL(avatar);
        const hasRealPath = parsed.pathname && parsed.pathname !== '/';
        return hasRealPath ? avatar : '';
    }
    catch (e) {
        return '';
    }
}
function getSettingsRequestParams() {
    const params = {};
    const clubId = ensureClubId();
    if (clubId) {
        params.c = clubId;
        return params;
    }
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const clubIdFromQuery = urlParams.get('c');
        if (clubIdFromQuery) {
            params.c = clubIdFromQuery;
            return params;
        }
    }
    return params;
}
export class BookingAPI {
    static async sendSmsLoginCode(phone) {
        try {
            const formData = new URLSearchParams();
            formData.set('phone', phone);
            const response = await http.post('sms-login', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            if (!isSuccessStatus(response.status)) {
                logFallback('sendSmsLoginCode', response.data);
                return {
                    status: 'error',
                    error: normalizeApiError(response.data?.error, 'Не удалось отправить код')
                };
            }
            if (response.data?.status === 'error') {
                return {
                    ...response.data,
                    error: normalizeApiError(response.data?.error, 'Не удалось отправить код')
                };
            }
            return response.data;
        }
        catch (error) {
            logFallback('sendSmsLoginCode', error);
            return {
                status: 'error',
                error: 'Не удалось отправить код'
            };
        }
    }
    static async verifySmsLoginCode(phone, code) {
        try {
            const formData = new URLSearchParams();
            formData.set('phone', phone);
            formData.set('code', code);
            const response = await http.post('sms-login', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            if (!isSuccessStatus(response.status)) {
                logFallback('verifySmsLoginCode', response.data);
                return {
                    status: 'error',
                    error: normalizeApiError(response.data?.error, 'Не удалось подтвердить код')
                };
            }
            if (response.data?.status === 'error') {
                return {
                    ...response.data,
                    error: normalizeApiError(response.data?.error, 'Не удалось подтвердить код')
                };
            }
            return response.data;
        }
        catch (error) {
            logFallback('verifySmsLoginCode', error);
            return {
                status: 'error',
                error: 'Не удалось подтвердить код'
            };
        }
    }
    static async registerUser(payload) {
        const clubId = ensureClubId({ clubId: payload.club });
        if (!payload?.token && !getStoredAuthToken()) {
            return {
                success: false,
                error: 'Не найден токен авторизации'
            };
        }
        try {
            const formData = new URLSearchParams();
            formData.set('name', payload.name);
            formData.set('surname', payload.surname);
            formData.set('patronymic', payload.patronymic || '');
            formData.set('b_day', payload.b_day || '');
            formData.set('sex', payload.sex || '');
            formData.set('phone', payload.phone);
            if (clubId) {
                formData.set('club', String(clubId));
            }
            const headers = {
                'Content-Type': 'application/x-www-form-urlencoded'
            };
            if (payload?.token) {
                headers.Authorization = `Bearer ${payload.token}`;
            }
            const response = await http.post('user', formData, {
                headers
            });
            if (!isSuccessStatus(response.status)) {
                logFallback('registerUser', response.data);
                return {
                    success: false,
                    error: normalizeApiError(
                        response.data?.message ?? response.data?.error,
                        'Не удалось завершить регистрацию'
                    )
                };
            }
            return {
                success: true,
                data: response.data
            };
        }
        catch (error) {
            logFallback('registerUser', error);
            return {
                success: false,
                error: error?.message || 'Не удалось завершить регистрацию'
            };
        }
    }
    static async getUser() {
        if (!getStoredAuthToken()) {
            return buildGuestUser();
        }
        try {
            const response = await http.get('user');
            if (!isSuccessStatus(response.status)) {
                const errorMessage = 'message' in response.data ? response.data.message : undefined;
                if (response.status !== 400 || errorMessage !== 'Пользователь не найден') {
                    logFallback('getUser', response.data);
                }
                return buildGuestUser();
            }
            if (!('id' in response.data)) {
                return buildGuestUser();
            }
            return {
                id: response.data.id,
                name: buildUserName(response.data),
                avatar: normalizeUserAvatar(response.data.avatar || response.data.photo || ''),
                contacts: response.data.contacts || [],
                depositFull: response.data.deposit_full || null
            };
        }
        catch (error) {
            logFallback('getUser', error);
            return buildGuestUser();
        }
    }
    static async getRentReservations() {
        if (!getStoredAuthToken() || !ensureClubId()) {
            return [];
        }
        try {
            const response = await http.get('reservation', {
                params: {
                    available: 1,
                    pageSize: -1
                }
            });
            if (!isSuccessStatus(response.status) || !Array.isArray(response.data)) {
                logFallback('getRentReservations', response.data);
                return [];
            }
            return response.data.filter((item) => item.type === 'rent');
        }
        catch (error) {
            logFallback('getRentReservations', error);
            return [];
        }
    }
    static async getReservation(id) {
        try {
            const response = await http.get(`reservation/${id}`);
            if (!isSuccessStatus(response.status) || !response.data || typeof response.data !== 'object') {
                return { success: false, error: normalizeApiError(response.data?.message, 'Не удалось загрузить бронирование') };
            }
            return { success: true, data: response.data };
        }
        catch (error) {
            return { success: false, error: normalizeApiError(error?.message, 'Не удалось загрузить бронирование') };
        }
    }
    static async cancelReservation(id) {
        try {
            const response = await http.delete(`schedule/${id}`);
            const data = response.data || {};
            if (!isSuccessStatus(response.status)) {
                return { success: false, error: normalizeApiError(data.message, 'Не удалось отменить бронирование') };
            }
            return data;
        }
        catch (error) {
            return { success: false, error: normalizeApiError(error?.message, 'Не удалось отменить бронирование') };
        }
    }
    static async getClub() {
        try {
            const clubId = ensureClubId();
            if (!clubId) {
                return buildGuestClub();
            }
            const response = await http.options('club');
            if (!isSuccessStatus(response.status) || !Array.isArray(response.data)) {
                logFallback('getClub', response.data);
                return buildGuestClub();
            }
            const club = response.data.find((item) => item.id === clubId) || response.data[0];
            if (!club) {
                return buildGuestClub();
            }
            return mapClub(club);
        }
        catch (error) {
            logFallback('getClub', error);
            return buildGuestClub();
        }
    }
    static async getBranches() {
        try {
            if (!ensureClubId()) {
                return [];
            }
            const response = await http.options('club');
            if (!isSuccessStatus(response.status) || !Array.isArray(response.data)) {
                logFallback('getBranches', response.data);
                return [];
            }
            return response.data.map(mapClub);
        }
        catch (error) {
            logFallback('getBranches', error);
            return [];
        }
    }
    static async changeCurrentClub(clubId) {
        if (!ensureClubId({ clubId }) || !getStoredAuthToken()) {
            return false;
        }
        try {
            const formData = new URLSearchParams();
            formData.set('club', String(clubId));
            const response = await http.post('user/current-club', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            if (!isSuccessStatus(response.status)) {
                logFallback('changeCurrentClub', response.data);
                return false;
            }
            return response.data?.status === 'success' || response.data?.success === true;
        }
        catch (error) {
            logFallback('changeCurrentClub', error);
            return false;
        }
    }
    static async getDirections() {
        try {
            const clubId = ensureClubId();
            if (!clubId) {
                return [];
            }
            const response = await http.get('subdivision', {
                params: {
                    filial: [clubId],
                    schedule_type: 'rent'
                }
            });
            if (!isSuccessStatus(response.status)) {
                logFallback('getDirections', response.data);
                return [];
            }
            return response.data.map((item) => ({
                id: item.id,
                name: item.name
            }));
        }
        catch (error) {
            logFallback('getDirections', error);
            return [];
        }
    }
    static async getAvailableDays(filters = {}) {
        const subdivisionId = getSelectedSubdivisionId(filters);
        const clubId = ensureClubId(filters);
        if (!subdivisionId || !clubId) {
            return [];
        }
        try {
            const params = {
                club_id: clubId,
                subdivision_id: subdivisionId,
                date_end: getBookingWindowEndDate(Number(filters.countWeek || 0))
            };
            const response = await http.get('schedule/rent/dates', {
                params
            });
            if (!isSuccessStatus(response.status) || !Array.isArray(response.data)) {
                logFallback('getAvailableDays', response.data);
                return [];
            }
            return response.data.map((date) => ({
                date,
                hasSlots: true
            }));
        }
        catch (error) {
            logFallback('getAvailableDays', error);
            return [];
        }
    }
    static async getTrainers() {
        return [];
    }
    static async getSettings() {
        try {
            if (!hasRuntimeClubId()) {
                logMissingClubId();
                return {};
            }
            const response = await http.get('setting', {
                params: getSettingsRequestParams()
            });
            if (!isSuccessStatus(response.status) || typeof response.data !== 'object' || response.data === null) {
                logFallback('getSettings', response.data);
                return {};
            }
            return response.data;
        }
        catch (error) {
            logFallback('getSettings', error);
            return {};
        }
    }
    static async getTimeSlots(filters = {}) {
        const subdivisionId = getSelectedSubdivisionId(filters);
        const clubId = ensureClubId(filters);
        if (!subdivisionId || !filters.date || !clubId) {
            return [];
        }
        try {
            const response = await http.get('schedule/rent/time-slots', {
                params: {
                    club_id: clubId,
                    subdivision_id: subdivisionId,
                    date: filters.date
                }
            });
            if (!isSuccessStatus(response.status) || !Array.isArray(response.data)) {
                logFallback('getTimeSlots', response.data);
                return [];
            }
            return response.data.map((slot, index) => ({
                id: index + 1,
                time: slot.time,
                minPrice: Number(slot.min_price?.value ?? slot.min_price),
                currency: slot.min_price?.currency
            }));
        }
        catch (error) {
            logFallback('getTimeSlots', error);
            return [];
        }
    }
    static async getCourtsWithDurations(filters = {}) {
        const subdivisionId = getSelectedSubdivisionId(filters);
        const clubId = ensureClubId(filters);
        if (!subdivisionId || !filters.date || !filters.time || !clubId) {
            return [];
        }
        try {
            const response = await http.get('schedule/rent/places', {
                params: {
                    club_id: clubId,
                    subdivision_id: subdivisionId,
                    date_time_start: `${filters.date} ${filters.time}`
                }
            });
            if (!isSuccessStatus(response.status) || !Array.isArray(response.data)) {
                logFallback('getCourtsWithDurations', response.data);
                return [];
            }
            return response.data.map((item) => ({
                id: item.place.id,
                name: item.place.name,
                description: item.place.description,
                images: item.place.images?.length ? item.place.images : [],
                amenities: (item.place.tags || []).map((tag, index) => ({
                    id: index + 1,
                    name: tag,
                    icon: 'star'
                })),
                durations: item.services.map((service) => ({
                    minutes: service.duration,
                    price: Number(service.price_to_pay?.value ?? service.price_to_pay),
                    currency: service.price_to_pay?.currency,
                    serviceId: service.service_id,
                    trainingId: service.training_id,
                    availableServiceId: service.available_service_id
                }))
            }));
        }
        catch (error) {
            logFallback('getCourtsWithDurations', error);
            return [];
        }
    }
    static async bookSlot(payload) {
        if (!ensureClubId()) {
            return {
                success: false
            };
        }
        try {
            const formData = new URLSearchParams();
            formData.set('training_id', String(payload.training_id));
            formData.set('place_id', String(payload.place_id));
            formData.set('service_id', String(payload.service_id));
            formData.set('date', payload.date);
            formData.set('time', payload.time);
            if (payload.deposit !== undefined) {
                formData.set('deposit', String(payload.deposit));
            }
            if (payload.promocode) {
                formData.set('promocode', payload.promocode);
            }
            if (payload.payment_type) {
                formData.set('payment_type', payload.payment_type);
            }
            if (payload.trainer_id !== undefined) {
                formData.set('trainer_id', String(payload.trainer_id));
            }
            const response = await http.post('schedule/rent/registration', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            if (!isSuccessStatus(response.status)) {
                logFallback('bookSlot', response.data);
                return {
                    success: false,
                    error: normalizeApiError(
                        response.data?.message ?? response.data?.error,
                        'Не удалось выполнить бронирование'
                    )
                };
            }
            return {
                success: true,
                bookingId: response.data.sign_id,
                status: response.data.status,
                requireVisitReason: response.data.require_visit_reason,
                debtItem: response.data.debt_item || null,
                registrationInfo: response.data.registration_info || null,
                autoDeleteTime: response.data?.registration_info?.auto_delete_time || null
            };
        }
        catch (error) {
            logFallback('bookSlot', error);
            return {
                success: false,
                error: normalizeApiError(error?.message, 'Не удалось выполнить бронирование')
            };
        }
    }
    static async getPaymentApiKey() {
        if (!ensureClubId()) {
            return '';
        }
        try {
            const response = await http.get('payment/apikey');
            if (!isSuccessStatus(response.status)) {
                logFallback('getPaymentApiKey', response.data);
                return '';
            }
            return response.data;
        }
        catch (error) {
            logFallback('getPaymentApiKey', error);
            return '';
        }
    }
    static async getPaymentCards() {
        if (!ensureClubId()) {
            return [];
        }
        try {
            const response = await http.get('payment/cards');
            if (!isSuccessStatus(response.status) || !Array.isArray(response.data)) {
                logFallback('getPaymentCards', response.data);
                return [];
            }
            return response.data;
        }
        catch (error) {
            logFallback('getPaymentCards', error);
            return [];
        }
    }
    static async checkPromocode(params = {}) {
        if (!ensureClubId()) {
            return { success: false, error: 'club-id is required' };
        }
        try {
            const response = await http.get('promocode', { params });
            if (!isSuccessStatus(response.status) || typeof response.data !== 'object' || response.data === null) {
                logFallback('checkPromocode', response.data);
                return { success: false, error: 'Не удалось применить промокод' };
            }
            return response.data;
        }
        catch (error) {
            logFallback('checkPromocode', error);
            return { success: false, error: 'Не удалось применить промокод' };
        }
    }
    static async createInvoice(payload) {
        if (!ensureClubId()) {
            return null;
        }
        try {
            const formData = new URLSearchParams();
            formData.set('orders', String(payload.orders));
            formData.set('savecard', String(payload.savecard ?? 1));
            formData.set('cloudcheck', String(payload.cloudcheck ?? 1));
            formData.set('withoutLink', String(payload.withoutLink ?? 1));
            if (payload.email) {
                formData.set('email', payload.email);
            }
            if (payload.returnUrl) {
                formData.set('returnUrl', payload.returnUrl);
            }
            const response = await http.post('payment/invoice', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            if (!isSuccessStatus(response.status) || typeof response.data !== 'object' || response.data === null) {
                logFallback('createInvoice', response.data);
                return null;
            }
            return response.data;
        }
        catch (error) {
            logFallback('createInvoice', error);
            return null;
        }
    }
    static async pay(payload) {
        if (!ensureClubId()) {
            return { success: false };
        }
        try {
            const formData = new URLSearchParams();
            formData.set('invoice_id', String(payload.invoice_id));
            formData.set('card_id', String(payload.card_id));
            const response = await http.post('payment/pay', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            if (!isSuccessStatus(response.status) || typeof response.data !== 'object' || response.data === null) {
                logFallback('pay', response.data);
                return { success: false };
            }
            return response.data;
        }
        catch (error) {
            logFallback('pay', error);
            return { success: false };
        }
    }
    static async payUseDeposit(payload) {
        if (!ensureClubId()) {
            return { success: false, error: 'club-id is required' };
        }
        try {
            const formData = new URLSearchParams();
            formData.set('debt_item_id', String(payload.debt_item_id));
            if (payload.deposit_amount !== undefined && payload.deposit_amount !== null) {
                formData.set('deposit_amount', String(payload.deposit_amount));
            }
            if (payload.promocode) {
                formData.set('promocode', payload.promocode);
            }
            const response = await http.post('payment/pay-use-deposit', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            if (!isSuccessStatus(response.status) || typeof response.data !== 'object' || response.data === null) {
                logFallback('payUseDeposit', response.data);
                return {
                    success: false,
                    error: normalizeApiError(response.data?.error ?? response.data?.message, 'Не удалось применить скидку')
                };
            }
            return response.data;
        }
        catch (error) {
            logFallback('payUseDeposit', error);
            return { success: false, error: 'Не удалось применить скидку' };
        }
    }
    static async checkInvoice(params = {}) {
        if (!ensureClubId()) {
            return false;
        }
        try {
            const response = await http.get('payment/check-by-invoice', { params });
            if (!isSuccessStatus(response.status)) {
                logFallback('checkInvoice', response.data);
                return false;
            }
            return response;
        }
        catch (error) {
            logFallback('checkInvoice', error);
            return false;
        }
    }
}
